/**
 * Native UI polish for the Capacitor shell:
 *   1. Hide the launch splash as soon as the web app has actually painted, so
 *      users don't stare at the splash for the config's fixed fallback duration
 *      when the app loaded quickly.
 *   2. Match the native status bar text and its background to the app's current
 *      theme so the clock/icons are always legible.
 *
 * Everything is derived from the *actually rendered* page background, so it can
 * never pick a status-bar contrast that hides the bar and it adapts to any theme
 * without hardcoding palette values. All calls no-op outside the Capacitor shell
 * and never throw — safe to call on every load (browser + MobiLoud unaffected).
 *
 * @capacitor/splash-screen, @capacitor/status-bar, and @capacitor/app are loaded
 * lazily (dynamic import) so they are excluded from the main entry bundle and
 * never downloaded/parsed in MobiLoud or plain web — only fetched when
 * isCapacitorEnvironment() is true.
 */

import Honeybadger from "@honeybadger-io/js";
import { isCapacitorEnvironment } from "./detection";

function reportUiError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_native_ui" },
    });
  } catch {
    // reporting must never break the app
  }
}

// Run `fn` after the browser has painted at least one frame. Two rAFs guarantee
// a real frame landed (the first fires before paint, the second after), so the
// splash hides on actual content and the status bar reads the applied theme.
function afterNextPaint(fn) {
  const raf =
    typeof requestAnimationFrame === "function"
      ? requestAnimationFrame
      : (cb) => setTimeout(cb, 0);
  raf(() => raf(fn));
}

// Parse the computed page background into { luminance 0–1, hex } — or null when
// it's transparent / unparseable, in which case we leave the device default
// rather than guess a contrast that could hide the status bar.
function pageBackground() {
  try {
    const target = document.body || document.documentElement;
    if (!target) {
      return null;
    }
    const bg = window.getComputedStyle(target).backgroundColor;
    const parts = bg && bg.match(/[\d.]+/g);
    if (!parts || parts.length < 3) {
      return null;
    }
    const [r, g, b] = parts.slice(0, 3).map(Number);
    const alpha = parts.length >= 4 ? Number(parts[3]) : 1;
    if (alpha === 0) {
      return null; // fully transparent → nothing meaningful to match
    }
    // sRGB-weighted perceived luminance — enough to choose light vs dark text.
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    const hex =
      "#" +
      [r, g, b]
        .map((c) => Math.round(c).toString(16).padStart(2, "0"))
        .join("");
    return { luminance, hex };
  } catch {
    return null;
  }
}

let splashHidden = false;

/**
 * Hide the launch splash. The shell keeps launchAutoHide=true as a safety net,
 * so a missed/failed call here can never strand the splash. Runs once; resets
 * on failure so a later call can retry. No-op off-shell.
 */
export function hideSplashOnReady() {
  if (!isCapacitorEnvironment() || splashHidden) {
    return;
  }
  splashHidden = true;
  import("@capacitor/splash-screen")
    .then(({ SplashScreen }) => SplashScreen.hide())
    .catch((error) => {
      splashHidden = false; // allow a retry if the hide failed
      reportUiError(error);
    });
}

/**
 * Sync the native status bar to the app's current theme by reading the real
 * rendered page background: light background → dark status-bar text; dark
 * background → light text. On both platforms we also paint the status-bar
 * background to match, since with overlaysWebView:false the iOS plugin covers the
 * status-bar area with an opaque view that would otherwise stay black.
 * No-op / non-throwing off-shell.
 * @returns {Promise<void>}
 */
export async function applyStatusBarStyle() {
  if (!isCapacitorEnvironment()) {
    return;
  }
  const bg = pageBackground();
  if (!bg) {
    return; // can't tell — keep the device default rather than guess wrong
  }
  const isLightBackground = bg.luminance > 0.5;
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({
      style: isLightBackground ? Style.Light : Style.Dark,
    });
    // Paint the status-bar backing color on BOTH platforms. With
    // overlaysWebView:false the iOS StatusBar plugin lays an opaque UIView over
    // the status-bar area (default black), so the webview never shows through —
    // setStyle alone leaves a black strip regardless of theme. setBackgroundColor
    // recolors that view to match the page. It's a registered, working iOS plugin
    // method; the "Android only" note in the JS wrapper docs is about the wrapper,
    // not a native limitation.
    await StatusBar.setBackgroundColor({ color: bg.hex });
  } catch (error) {
    reportUiError(error);
  }
}

let resumeListenerRegistered = false;

// Re-apply the status bar on every foreground so a theme change made while
// backgrounded (in-app toggle or an OS light/dark switch) is reflected on return.
function registerResumeSync() {
  if (resumeListenerRegistered) {
    return;
  }
  resumeListenerRegistered = true;
  import("@capacitor/app")
    .then(({ App }) => {
      try {
        const result = App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) {
            applyStatusBarStyle();
          }
        });
        if (result && typeof result.catch === "function") {
          result.catch((error) => {
            resumeListenerRegistered = false; // allow a later call to retry
            reportUiError(error);
          });
        }
      } catch (error) {
        resumeListenerRegistered = false;
        reportUiError(error);
      }
    })
    .catch((error) => {
      resumeListenerRegistered = false;
      reportUiError(error);
    });
}

/**
 * Initialize native UI polish. Call once after the first page has mounted.
 * Registers the resume sync immediately, then hides the splash and applies the
 * status-bar style after the first real paint. No-op off-shell.
 */
export function initNativeUi() {
  if (!isCapacitorEnvironment()) {
    return;
  }
  registerResumeSync();
  afterNextPaint(() => {
    hideSplashOnReady();
    applyStatusBarStyle();
  });
}
